import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ListingCard from './ListingCard';
import SeoHead from './SeoHead';
import { locationMeta, getLocationContent, generatePropertySlug } from '../seoUtils';
import { ArrowRightIcon, MapPinIcon, HomeIcon } from './IconSet';
import { Breadcrumbs, LoadingSpinner } from './ComponentLibrary';

/**
 * Normalise a URL slug back into a state name.
 * E.g. "lagos" → "Lagos", "akwa-ibom" → "Akwa Ibom", "fct" → "FCT"
 */
function slugToState(slug) {
  if (!slug) {return '';}
  const lower = slug.toLowerCase().replace(/-/g, ' ');
  if (lower === 'fct' || lower === 'abuja') {return 'FCT';}
  return lower
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Parse a filter slug like "apartments", "houses-for-rent", "for-sale".
 * Returns { propertyType, listingType } (both possibly empty).
 */
function parseFilterSlug(slug) {
  if (!slug) {return { propertyType: '', listingType: '' };}

  const s = slug.toLowerCase();

  // Listing-type keywords
  let listingType = '';
  if (s.includes('for-sale') || s.includes('for-buy')) {listingType = 'buy';}
  else if (s.includes('for-rent') || s.includes('long-term-rent') || s.includes('longterm-rent'))
  {listingType = 'rent';}
  else if (s.includes('short-let')) {listingType = 'short-let';}

  // Property type keywords (singular mapping)
  const typeMap = {
    apartments: 'Apartment',
    apartment: 'Apartment',
    houses: 'House',
    house: 'House',
    duplexes: 'Duplex',
    duplex: 'Duplex',
    bungalows: 'Bungalow',
    bungalow: 'Bungalow',
    flats: 'Flat',
    flat: 'Flat',
    rooms: 'Room',
    room: 'Room',
    terraces: 'Terrace',
    terrace: 'Terrace',
    penthouses: 'Penthouse',
    penthouse: 'Penthouse',
    land: 'Land',
    commercial: 'Commercial',
    garage: 'Garage',
  };

  let propertyType = '';
  for (const [keyword, value] of Object.entries(typeMap)) {
    if (s.includes(keyword)) {
      propertyType = value;
      break;
    }
  }

  return { propertyType, listingType };
}

function LocationLandingPage({ properties = [], loading, error, onOpenProperty }) {
  const { state: stateSlug, filter: filterSlug } = useParams();
  const navigate = useNavigate();

  const stateName = slugToState(stateSlug);
  const { propertyType, listingType } = useMemo(() => parseFilterSlug(filterSlug), [filterSlug]);

  // Filter properties to this location + optional type / listing-type
  const filtered = useMemo(() => {
    const isNationwide = stateName.toLowerCase() === 'nigeria';

    return properties.filter((p) => {
      const pState = (p.state || '').toLowerCase();
      const targetState = stateName.toLowerCase();
      if (!isNationwide && pState !== targetState) {return false;}

      if (propertyType) {
        const pType = (p.propertyType || p.property_type || '').toLowerCase();
        if (pType !== propertyType.toLowerCase()) {return false;}
      }

      if (listingType) {
        const lt = (p.listing_type || p.type || '').toLowerCase();
        if (lt !== listingType) {return false;}
      }

      return true;
    });
  }, [properties, stateName, propertyType, listingType]);

  // Build SEO meta. `path` is the real current route, not re-derived from
  // state/listingType - see the comment on locationMeta() for why that
  // matters (a re-derived path silently pointed the canonical tag at the
  // wrong URL for property-type-only filters like /lagos/apartments).
  const seo = useMemo(
    () =>
      locationMeta({
        state: stateName,
        listingType: listingType || undefined,
        propertyType: propertyType || undefined,
        path: `/${stateSlug}${filterSlug ? `/${filterSlug}` : ''}`,
      }),
    [stateName, listingType, propertyType, stateSlug, filterSlug],
  );

  // Build page title from filter context
  const pageTitle = useMemo(() => {
    const parts = [];
    if (propertyType) {parts.push(`${propertyType}s`);}
    else {parts.push('Properties');}

    if (listingType === 'buy') {parts.push('for Sale');}
    else if (listingType === 'short-let') {parts.push('Short-let');}
    else if (listingType === 'rent') {parts.push('for Rent');}

    parts.push(`in ${stateName}`);
    return parts.join(' ');
  }, [propertyType, listingType, stateName]);

  const locationBlurb = getLocationContent(stateName);

  const breadcrumbItems = useMemo(
    () => [
      { label: 'Home', href: '/' },
      { label: 'Browse', href: '/browse' },
      { label: stateName, current: true },
    ],
    [stateName],
  );

  // Quick-link suggestions
  const quickLinks = useMemo(() => {
    const base = `/${stateSlug}`;
    const links = [];
    if (filterSlug !== 'apartments')
    {links.push({ href: `${base}/apartments`, label: `Apartments in ${stateName}` });}
    if (filterSlug !== 'houses-for-rent')
    {links.push({ href: `${base}/houses-for-rent`, label: `Houses for rent in ${stateName}` });}
    if (filterSlug !== 'long-term-rent')
    {links.push({ href: `${base}/long-term-rent`, label: `Long-term rent in ${stateName}` });}
    if (filterSlug !== 'short-let')
    {links.push({ href: `${base}/short-let`, label: `Short-let in ${stateName}` });}
    if (filterSlug !== 'for-sale')
    {links.push({ href: `${base}/for-sale`, label: `Properties for sale in ${stateName}` });}
    if (filterSlug !== 'duplexes')
    {links.push({ href: `${base}/duplexes`, label: `Duplexes in ${stateName}` });}
    if (filterSlug !== 'land')
    {links.push({ href: `${base}/land`, label: `Land for sale in ${stateName}` });}
    return links.slice(0, 6);
  }, [stateSlug, filterSlug, stateName]);

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [stateSlug, filterSlug]);

  return (
    <>
      <SeoHead title={seo.fullTitle} description={seo.desc} url={seo.url} image={seo.img} />

      <Breadcrumbs items={breadcrumbItems} />

      <section className="section location-landing-hero">
        <div className="location-landing-header">
          <MapPinIcon size={28} />
          <h1>{pageTitle}</h1>
        </div>
        <p className="location-landing-blurb">{locationBlurb}</p>
      </section>

      {/* Quick filter links */}
      <section className="section location-quick-links">
        <h2 className="location-quick-links__title">Browse by type</h2>
        <div className="location-quick-links__grid">
          {quickLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="location-quick-link"
              onClick={(e) => {
                e.preventDefault();
                navigate(link.href);
              }}
            >
              <HomeIcon size={16} />
              {link.label}
              <ArrowRightIcon size={14} />
            </a>
          ))}
        </div>
      </section>

      {/* Listings */}
      <section className="section">
        {loading && <LoadingSpinner size="lg" label="Loading listings..." />}
        {error && <p className="error-text">⚠️ {error}</p>}

        {!loading && !error && filtered.length === 0 && (
          <div className="location-empty">
            <p>No listings match this filter yet. Try browsing all properties in {stateName}:</p>
            <a
              href={`/${stateSlug}`}
              className="btn btn-primary"
              onClick={(e) => {
                e.preventDefault();
                navigate(`/${stateSlug}`);
              }}
            >
              All properties in {stateName}
            </a>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <>
            <p className="location-result-count">
              {filtered.length} {filtered.length === 1 ? 'property' : 'properties'} found
            </p>
            <div className="listing-grid">
              {filtered.slice(0, 24).map((item) => (
                <ListingCard
                  key={item.id}
                  item={item}
                  onOpenProperty={() => {
                    if (onOpenProperty) {onOpenProperty(item.id);}
                    else {navigate(`/property/${generatePropertySlug(item)}`);}
                  }}
                />
              ))}
            </div>
          </>
        )}

        {filtered.length > 24 && (
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/browse')}>
              View all on Browse page <ArrowRightIcon size={14} />
            </button>
          </div>
        )}
      </section>
    </>
  );
}

export default LocationLandingPage;
